import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
    Table, 
    Card, 
    Button, 
    Space, 
    Tag, 
    Typography,
    Avatar,
    Alert,
    Radio,
    Tooltip,
    Grid,
    Select,
    Input
} from 'antd';
import { 
    EyeOutlined, 
    UserOutlined,
    FileTextOutlined,
    PhoneOutlined,
    MailOutlined,
    FilterOutlined,
    CalendarOutlined,
    BankOutlined,
    SearchOutlined
} from '@ant-design/icons';
import { SelfRegistrationSessionDTO } from '@/shared/api/model';
import { useSelfRegistration } from '../hooks/useSelfRegistration';

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

type ReviewFilter = 'all' | 'direct-deposit';

const OfficerReviewView: React.FC = () => {
    const navigate = useNavigate();
    const screens = useBreakpoint();
    const isMobile = !screens.md;
    const [reviewFilter, setReviewFilter] = useState<ReviewFilter>('all');
    const [zoneFilter, setZoneFilter] = useState<string | undefined>(undefined);
    const [searchName, setSearchName] = useState('');
    const [searchNidaId, setSearchNidaId] = useState('');
    const [searchEmail, setSearchEmail] = useState('');

    // API hooks
    const { 
        pendingReviews,
        isLoadingPendingReviews
    } = useSelfRegistration({ searchName, searchNidaId, searchEmail, enablePendingReviews: true });

    // Filter applications based on selected filter
    const rawPending = (pendingReviews as any);
    const allApplications: SelfRegistrationSessionDTO[] = Array.isArray(rawPending)
        ? rawPending
        : Array.isArray(rawPending?.data)
            ? rawPending.data
            : Array.isArray(rawPending?.data?.data)
                ? rawPending.data.data
                : [];

    const getZoneName = (application: SelfRegistrationSessionDTO): string | null => {
        if (!application.locationDataJson) return null;
        try {
            const parsed = JSON.parse(application.locationDataJson as any);
            return parsed?.locationSelections?.zone?.name ?? null;
        } catch {
            return null;
        }
    };

    const availableZones = useMemo(() => {
        const zones = new Set<string>();
        allApplications.forEach((app) => {
            const zoneName = getZoneName(app);
            if (zoneName) zones.add(zoneName);
        });
        return Array.from(zones).sort();
    }, [allApplications]);

    const applications = reviewFilter === 'direct-deposit' 
        ? allApplications.filter(app => 
            app.currentStep === 'DEPOSIT' && 
            app.directDepositPendingReview === true
          )
        : allApplications;

    const zoneFilteredApplications = zoneFilter
        ? applications.filter(app => {
            const zoneName = getZoneName(app);
            return zoneName != null && zoneName.toLowerCase() === zoneFilter.toLowerCase();
        })
        : applications;
    
    const isLoading = isLoadingPendingReviews;

    // Count applications by type for display
    const directDepositCount = allApplications.filter(app => 
        app.currentStep === 'DEPOSIT' && app.directDepositPendingReview === true
    ).length;
    const totalCount = allApplications.length;

    const handleViewApplication = (application: SelfRegistrationSessionDTO) => {
        // Navigate to the detailed review page
        navigate(`/registration/review/${application.sessionToken}`);
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'PENDING_OFFICER_REVIEW': return 'orange';
            case 'OFFICER_APPROVED': return 'green';
            case 'REJECTED': return 'red';
            case 'COMPLETED': return 'green';
            case 'ACTIVE': return 'blue';
            default: return 'blue';
        }
    };

    const columns = [
        {
            title: 'Applicant',
            key: 'applicant',
            render: (record: SelfRegistrationSessionDTO) => (
                <Space>
                    <Avatar icon={<UserOutlined />} />
                    <div>
                        <Text strong>{record.fullName}</Text>
                        <br />
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                            <PhoneOutlined /> {record.phoneNumber}
                        </Text>
                    </div>
                </Space>
            )
        },
        {
            title: 'Contact',
            key: 'contact',
            render: (record: SelfRegistrationSessionDTO) => (
                <Space direction="vertical" size="small">
                    <Text style={{ fontSize: '13px' }}>
                        <MailOutlined /> {record.email}
                    </Text>
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                        <CalendarOutlined /> {new Date(record.createdAt || '').toLocaleDateString()}
                    </Text>
                </Space>
            )
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status: string) => (
                <Tag color={getStatusColor(status)}>
                    {status?.replace('_', ' ')}
                </Tag>
            )
        },
        {
            title: 'Employer',
            key: 'employer',
            render: (record: SelfRegistrationSessionDTO) => (
                record.employerName ? (
                    <Space direction="vertical" size={0}>
                        <Text style={{ fontSize: '13px' }}>{record.employerName}</Text>
                        {record.employerId ? (
                            <Tag color="green" style={{ fontSize: '11px' }}>Linked</Tag>
                        ) : (
                            <Tag color="orange" style={{ fontSize: '11px' }}>Manual Entry</Tag>
                        )}
                    </Space>
                ) : (
                    <Text type="secondary">Not provided</Text>
                )
            )
        },
        {
            title: 'Documents',
            key: 'documents',
            render: (record: SelfRegistrationSessionDTO) => (
                <Space>
                    <FileTextOutlined />
                    <Text>{record.uploadedDocuments?.length || 0}</Text>
                </Space>
            )
        },
        {
            title: 'Direct Deposit',
            key: 'directDeposit',
            render: (record: SelfRegistrationSessionDTO) => (
                record.directDepositPendingReview ? (
                    <Tag color="orange" icon={<BankOutlined />}>
                        Pending Review
                    </Tag>
                ) : record.directDepositAmount ? (
                    <Tag color="green" icon={<BankOutlined />}>
                        TZS {record.directDepositAmount.toLocaleString()}
                    </Tag>
                ) : (
                    <Text type="secondary">No deposit</Text>
                )
            )
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (record: SelfRegistrationSessionDTO) => (
                <Button
                    type="primary"
                    icon={<EyeOutlined />}
                    onClick={() => handleViewApplication(record)}
                    size="small"
                >
                    Review
                </Button>
            )
        }
    ];

    return (
        <div style={{ padding: isMobile ? '16px' : '24px' }}>
            <Card>
                <div style={{ marginBottom: '20px' }}>
                    <Title level={3}>Self-Registration Applications</Title>
                    <Text type="secondary">
                        Review and approve pending self-registration applications. Click "Review" to view full details and take action.
                    </Text>
                </div>

                {/* Filter Controls */}
                <div style={{ 
                    marginBottom: '16px', 
                    padding: '12px', 
                    backgroundColor: '#fafafa', 
                    borderRadius: '6px' 
                }}>
                    <Space align="center" wrap>
                        <FilterOutlined />
                        <Text strong>Filter by:</Text>
                        <Radio.Group 
                            value={reviewFilter} 
                            onChange={(e) => setReviewFilter(e.target.value)}
                            buttonStyle="solid"
                            size={isMobile ? 'small' : 'middle'}
                        >
                            <Radio.Button value="all">
                                All Reviews ({totalCount})
                            </Radio.Button>
                            <Radio.Button value="direct-deposit">
                                <Tooltip title="Sessions in DEPOSIT step with pending direct deposit evidence">
                                    Direct Deposit Evidence ({directDepositCount})
                                </Tooltip>
                            </Radio.Button>
                        </Radio.Group>
                        {reviewFilter === 'direct-deposit' && directDepositCount > 0 && (
                            <Tag color="orange">Priority Reviews</Tag>
                        )}

                        <Text strong style={{ marginLeft: isMobile ? 0 : 8 }}>Zone:</Text>
                        <Select
                            allowClear
                            placeholder="All Zones"
                            style={{ width: isMobile ? 160 : 200 }}
                            value={zoneFilter}
                            onChange={(value) => setZoneFilter(value)}
                            options={availableZones.map((z) => ({ label: z, value: z }))}
                        />
                    </Space>

                    <Space wrap style={{ marginTop: 12 }}>
                        <SearchOutlined />
                        <Text strong>Search:</Text>
                        <Input
                            placeholder="Applicant name"
                            allowClear
                            style={{ width: isMobile ? 160 : 200 }}
                            value={searchName}
                            onChange={(e) => setSearchName(e.target.value)}
                        />
                        <Input
                            placeholder="NIDA / National ID"
                            allowClear
                            style={{ width: isMobile ? 160 : 200 }}
                            value={searchNidaId}
                            onChange={(e) => setSearchNidaId(e.target.value)}
                        />
                        <Input
                            placeholder="Email address"
                            allowClear
                            style={{ width: isMobile ? 160 : 220 }}
                            value={searchEmail}
                            onChange={(e) => setSearchEmail(e.target.value)}
                        />
                    </Space>
                </div>

                {/* Applications Table */}
                <Table
                    columns={columns}
                    dataSource={zoneFilteredApplications}
                    rowKey="sessionToken"
                    loading={isLoading}
                    pagination={{
                        pageSize: 10,
                        showSizeChanger: true,
                        showTotal: (total) => `Total ${total} applications`,
                        responsive: true
                    }}
                    scroll={{ x: isMobile ? 800 : undefined }}
                    // @ts-ignore
                    size={isMobile ? 'small' : 'default'}
                />

                {/* Empty State */}
                {!isLoading && zoneFilteredApplications.length === 0 && (
                    <Alert
                        message="No Applications Found"
                        description={
                            reviewFilter === 'direct-deposit' 
                                ? "No direct deposit evidence pending review."
                                : "No applications are currently pending review."
                        }
                        type="info"
                        showIcon
                        style={{ marginTop: '20px' }}
                    />
                )}
            </Card>
        </div>
    );
};

export default OfficerReviewView;
